/**
 * Firestore access for `skidaCrmPosts` — the publishing plan.
 *
 * Small collection, loaded whole and kept live. A publishing plan is a working
 * calendar of tens of rows, not a data set; paging it would cost more in
 * complexity than it saves in bytes.
 *
 * Links are stored in one direction only — the post holds `linkedEventId` and
 * `linkedAmbassadorIds`. The handoff is explicit that the reverse views (an
 * event's posts, an ambassador's posts) should be *queried*, not stored twice:
 * `where('linkedEventId','==',…)` and
 * `where('linkedAmbassadorIds','array-contains',…)`. Those helpers are here so
 * the other screens never reach for the whole collection.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { toIsoOrNull } from '@/shared/util/dates'
import {
  CHANNELS,
  CONTENT_TYPES,
  type Channel,
  type ContentType,
  type Post,
  type PostAsset,
  type PostStatus,
} from '@/types/posts'

const COLLECTION = 'skidaCrmPosts'

const str = (v: unknown) => (typeof v === 'string' ? v : '')

function mapAsset(v: DocumentData, i: number): PostAsset {
  const kind = str(v.kind)
  return {
    id: str(v.id) || `a${i}`,
    kind: kind === 'image' || kind === 'video' ? kind : 'text',
    title: str(v.title),
    text: str(v.text),
    url: str(v.url),
  }
}

function mapPost(d: QueryDocumentSnapshot<DocumentData>): Post {
  const v = d.data()
  // Unknown channel names are dropped rather than rendered: the dot column
  // depends on every value being one of the six, and a stray string would
  // either crash the lookup or add a seventh slot and shift every column.
  const channels = (Array.isArray(v.channels) ? v.channels : [])
    .map(String)
    .filter((c): c is Channel => (CHANNELS as readonly string[]).includes(c))

  const type = str(v.type)
  const status = str(v.status)

  return {
    id: d.id,
    date: str(v.date),
    time: str(v.time),
    channels,
    type: (CONTENT_TYPES as readonly string[]).includes(type)
      ? (type as ContentType)
      : 'Post',
    // A post written before Idea existed is a draft, which is what it was.
    status:
      status === 'idea' || status === 'planned' || status === 'published'
        ? (status as PostStatus)
        : 'draft',
    owner: str(v.owner),
    link: str(v.link),
    assets: Array.isArray(v.assets) ? (v.assets as DocumentData[]).map(mapAsset) : [],
    linkedEventId: str(v.linkedEventId),
    linkedAmbassadorIds: Array.isArray(v.linkedAmbassadorIds)
      ? (v.linkedAmbassadorIds as unknown[]).map(String)
      : [],
    linkedPartnerIds: Array.isArray(v.linkedPartnerIds)
      ? (v.linkedPartnerIds as unknown[]).map(String)
      : [],
    subject: str(v.subject),
    goal: str(v.goal),
    targetAudience: str(v.targetAudience),
    forPro: v.forPro === true,
    forApp: v.forApp === true,
    notes: str(v.notes),
    createdAt: toIsoOrNull(v.createdAt),
    updatedAt: toIsoOrNull(v.updatedAt),
  }
}

/** Live list, newest scheduled first. */
export function subscribeToPosts(
  onData: (posts: Post[]) => void,
  onError: (e: Error) => void,
): () => void {
  return onSnapshot(
    query(collection(db, COLLECTION)),
    (snap) => {
      const posts = snap.docs.map(mapPost)
      // Sorted here rather than by Firestore: `date` is missing on a draft
      // nobody has scheduled yet, and an orderBy would drop those documents
      // entirely instead of showing them.
      posts.sort((a, b) => (b.date || '').localeCompare(a.date || '') || a.time.localeCompare(b.time))
      onData(posts)
    },
    (e) => onError(e as Error),
  )
}

type PostInput = Omit<Post, 'id' | 'createdAt' | 'updatedAt'>

export async function createPost(input: PostInput): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    ...input,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function updatePost(id: string, patch: Partial<PostInput>): Promise<void> {
  await setDoc(
    doc(db, COLLECTION, id),
    { ...patch, updatedAt: serverTimestamp() },
    { merge: true },
  )
}

export async function deletePost(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id))
}

/**
 * The posts promoting one event.
 *
 * A query rather than a client-side filter of everything, so the events screen
 * never has to load the publishing plan to show three rows.
 */
export async function postsForEvent(eventId: string): Promise<Post[]> {
  const snap = await getDocs(
    query(collection(db, COLLECTION), where('linkedEventId', '==', eventId)),
  )
  return snap.docs.map(mapPost)
}

/** The posts crediting one ambassador. */
export async function postsForAmbassador(personId: string): Promise<Post[]> {
  const snap = await getDocs(
    query(collection(db, COLLECTION), where('linkedAmbassadorIds', 'array-contains', personId)),
  )
  return snap.docs.map(mapPost)
}

/**
 * The posts run for one partner.
 *
 * Queried rather than stored on the partner, for the same reason as the other
 * two: one direction of truth. A partner asking "what did you run for us this
 * season" is answered from the posts, which are the things that actually
 * happened.
 */
export async function postsForPartner(partnerId: string): Promise<Post[]> {
  const snap = await getDocs(
    query(collection(db, COLLECTION), where('linkedPartnerIds', 'array-contains', partnerId)),
  )
  return snap.docs.map(mapPost)
}
